class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    this.querySelector('form').addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);

    // Custom sorting inputs: clicking should ignore existing params and only set sort_by=value
    this.initSortInputs();

    // Sync sort inputs initially based on URL so visual state persists on first load
    FacetFiltersForm.syncSortInputsFromURL(this);

    // Immediate sync of existing inputs' checked attribute for styling
    FacetFiltersForm.applyCheckedAttribute(this);
    FacetFiltersForm.updateActiveFilterCount();

    // Listen for direct user changes on any checkbox/radio inside this component and mirror attribute
    this.addEventListener('change', (e) => {
      const target = e.target;
      if (!(target instanceof HTMLInputElement)) return;
      if (target.type === 'checkbox' || target.type === 'radio') {
        if (target.checked) {
          target.setAttribute('checked', 'checked');
        } else {
          target.removeAttribute('checked');
        }
        FacetFiltersForm.updateActiveFilterCount();
      }
    });
  }

  initSortInputs() {
    const sortInputs = this.querySelectorAll('.sort_by_input');
    if (!sortInputs.length) return;
    sortInputs.forEach((input) => {
      // Avoid double-binding after AJAX replacements
      if (input.dataset.sortBound === 'true') return;
      input.dataset.sortBound = 'true';
      const handler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const value = input.value;
        // Update visual state
        sortInputs.forEach((i) => i !== input && i.removeAttribute('checked'));
        input.setAttribute('checked', 'checked');

        // Preserve existing query params, replace ONLY sort_by
        const params = new URLSearchParams(window.location.search);
        // Remove existing sort_by instances
        params.delete('sort_by');
        // Reset pagination when resorting
        params.delete('page');
        // Set new sort
        params.set('sort_by', value);

        // Render with new combined params
        FacetFiltersForm.renderPage(params.toString(), e);
      };
      input.addEventListener('click', handler);
      input.addEventListener('change', handler);
    });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    searchParams = searchParams + '&' + 'filter.v.availability=1';
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }
    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        // FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.updateActiveFilterCount();
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    // FacetFiltersForm.renderProductCount(html);
    FacetFiltersForm.updateActiveFilterCount();
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;
    AOS.refreshHard();
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter'
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter((element) => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      const target = document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`);
      if (!target) return;
      target.innerHTML = element.innerHTML;
      // Ensure "checked" attribute present on active inputs for styling
      FacetFiltersForm.applyCheckedAttribute(target);
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));

    // After all replacements, ensure attributes are correct across the entire form (including mobile duplicate structure)
    const rootForm = document.getElementById('FacetFiltersForm');
    if (rootForm) FacetFiltersForm.applyCheckedAttribute(rootForm);
    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    if (mobileForm) FacetFiltersForm.applyCheckedAttribute(mobileForm);
    FacetFiltersForm.updateActiveFilterCount();
  }

  static applyCheckedAttribute(scopeEl = document) {
    // For any checkbox/radio inside scope, mirror its current state to a literal checked="checked" attribute (or remove)
    const inputs = scopeEl.querySelectorAll('input[type="checkbox"], input[type="radio"]');
    inputs.forEach((input) => (input.checked ? input.setAttribute('checked', 'checked') : null));
  }

  static syncSortInputsFromURL(root = document) {
    const params = new URLSearchParams(window.location.search);
    const currentSort = params.get('sort_by');
    const sortInputs = root.querySelectorAll('.sort_by_input');
    if (!sortInputs.length) return;
    let matched = false;
    sortInputs.forEach((input) => {
      if (input.value === currentSort) {
        const params = new URLSearchParams(window.location.search);
        input.setAttribute('checked', 'checked');
        matched = true;
      } else {
        input.checked = false;
        input.removeAttribute('checked');
      }
    });
    if (!matched) {
      const firstChecked = Array.from(sortInputs).find((i) => i.checked);
      if (firstChecked) firstChecked.setAttribute('checked', 'checked');
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();

    // Re-bind sort input handlers & sync their state after .sorting markup replaced
    const facetForm = document.querySelector('facet-filters-form');
    if (facetForm) {
      facetForm.initSortInputs();
      FacetFiltersForm.syncSortInputsFromURL(facetForm);
    }
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static updateActiveFilterCount(root = document) {
    const params = new URLSearchParams(window.location.search);
    const skip = new Set(['sort_by', 'page', 'q', 'options[prefix]', 'filter.v.availability']);
    const unique = new Set();
    let hasPrice = false;
    for (const [k, v] of params.entries()) {
      if (skip.has(k)) continue;
      if (k.includes('price')) {
        hasPrice = true;
        continue;
      }
      unique.add(k + '=' + v);
    }
    if (hasPrice) unique.add('price_range');
    const count = unique.size;
    const mobileLink = root.querySelector('.mobile-facets__clear');
    FacetFiltersForm._applyCountToLink(mobileLink, count);
    const desktopLink = root.querySelector('.active-facets__button-remove');
    FacetFiltersForm._applyCountToLink(desktopLink, count);
  }

  static _applyCountToLink(link, count) {
    if (!link) return;
    let span = link.querySelector('.active-filter_count');
    if (count > 0) {
      if (!span) {
        span = document.createElement('span');
        span.className = 'active-filter_count';
        link.appendChild(span);
      }
      span.textContent = ` (${count}) `;
    } else if (span) {
      span.remove();
    }
  }

  static resetActiveFilterCount(root = document) {
    const spans = root.querySelectorAll('.active-filter_count');
    if (!spans.length) return;
    spans.forEach((span) => span.remove());
  }

  static getSections() {
    return [
      {
        section: document.getElementById('ProductGridContainer').querySelector('#product-grid').dataset.id,
      },
    ];
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const formData = new FormData(event.target.closest('form'));
    const searchParams = new URLSearchParams(formData).toString();
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) =>
      element.addEventListener('change', this.onRangeChange.bind(this))
    );

    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.querySelector('a').addEventListener('click', (event) => {
      event.preventDefault();
      const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
      // Immediately reflect cleared state in UI before async render finishes
      FacetFiltersForm.resetActiveFilterCount();
      form.onActiveFilterClick(event);
    });
  }
}

customElements.define('facet-remove', FacetRemove);
